import React from "react";
import {
  formatLocalizedNumber,
  parseLocalizedNumber,
  sanitizeLocalizedNumberInput
} from "shared/utils/numberInputUtils";
import { numberDecimalSeparator } from "shared/utils/numberUtils";
import { InputBase } from "./Atoms/formInputs/InputBase";

export interface NumericValue {
  value: number
  formatted: string
}

export interface Props {
  innerRef?: React.Ref<HTMLInputElement>
  value: number | string
  onChanged: (newValue: NumericValue) => void
  decimalPoints: number

  loading?: boolean

  onEnterPressed?: (newValue: NumericValue) => void
  onEscPressed?: () => void
  canBeEmpty?: boolean
  error?: boolean
  width?: number
  canBeNegative?: boolean
  maxLength?: number
  defaultWhenError?: number
  id?: string
  minValue?: number
  maxValue?: number
  placeholder?: string
  focusOnMount?: boolean
  rightLabel?: string
}

const NumberInput = (props: Props) => {
  const formatNumber = (value: number | string) => formatLocalizedNumber(
    value,
    serverReference.culture.uiLocale,
    {
      decimalPoints: props.decimalPoints,
      preserveEmptyValue: props.canBeEmpty
    }
  );

  const [input, setInput] = React.useState(formatNumber(props.value));
  const [hasPasted, setHasPasted] = React.useState(false);

  React.useEffect(() => {
    setInput(formatNumber(props.value));
  }, [props.value]);

  const onPaste = () => {
    setHasPasted(true);
  };

  const onChange = (newInput: string) => {
    let sanitizedInput = sanitizeLocalizedNumberInput(newInput, serverReference.culture.uiLocale, {
      decimalPoints: props.decimalPoints,
      canBeNegative: props.canBeNegative,
      acceptAlternateDecimalSeparators: false
    });

    if (hasPasted) {
      setHasPasted(false);
      if (sanitizedInput === numberDecimalSeparator(serverReference.culture.uiLocale) || sanitizedInput === "") {
        sanitizedInput = "0";
      } else if (sanitizedInput[sanitizedInput.length - 1] === numberDecimalSeparator(serverReference.culture.uiLocale)) {
        sanitizedInput = sanitizedInput.substring(0, sanitizedInput.length - 1);
      }

      const parsedNumber = parseLocalizedNumber(sanitizedInput, serverReference.culture.uiLocale, {
        defaultWhenError: props.defaultWhenError,
        minValue: props.minValue,
        maxValue: props.maxValue
      });

      setInput(formatNumber(parsedNumber));
    } else {
      setInput(sanitizedInput);
    }
  };

  const onBlur = () => {
    if (props.canBeEmpty && input === "") {
      const newResult: NumericValue = {
        formatted: "",
        value: undefined
      };

      props.onChanged(newResult);
      return newResult;
    }

    const numericValue = parseLocalizedNumber(input, serverReference.culture.uiLocale, {
      defaultWhenError: props.defaultWhenError,
      minValue: props.minValue,
      maxValue: props.maxValue
    });
    const formatted = formatNumber(numericValue);

    const newResult: NumericValue = {
      formatted,
      value: numericValue
    };
    props.onChanged(newResult);

    setInput(formatted);

    return newResult;
  };

  const onKeyDown = (evt) => {
    if (evt.key === "Enter") {
      const newResult = onBlur();
      props.onEnterPressed?.(newResult);
    }

    if (evt.key === "Escape") {
      props.onEscPressed?.();
    }
  };

  return <InputBase
    loading={props.loading}
    id={props.id}
    ref={props.innerRef}
    onPaste={onPaste}
    value={input}
    onChange={value => { onChange(value); }}
    onBlur={onBlur}
    onKeyDown={onKeyDown}
    error={props.error}
    width={props.width}
    maxLength={props.maxLength}
    rightComponent={props.rightLabel ? {
      position: "right",
      type: "text",
      label: props.rightLabel,
    } : undefined}
    placeholder={props.placeholder}
    focusOnMount={props.focusOnMount}
  />;
};

export default NumberInput;
